import { Select, Tag } from 'antd'
import CantileverButton from 'components/CantileverButton'
import { WebGLLayer } from 'features/assetDatabase'
import { AssetViz, AssetVizMode } from 'features/assetViz'
import { DimensionsIcon } from 'features/libra/stepComponents/niiReport/cards/DimensionsIcon'
import { computeArea } from 'features/libra/stepComponents/niiReport/cards/totalModelArea'
import { ZoneRow as ZoneRowComponent } from 'features/libra/stepComponents/niiReport/cards/ZoneRow'
import { NiiSourcesZoneParams } from 'features/libra/stepComponents/niiReport/niiReportApi'
import { useNiiReportData } from 'features/libra/stepComponents/niiReport/NiiReportDataContext'
import {
  calculateZoneArea,
  createDrawingLayer,
  markupRectToZoneBounds,
  zonesToMarkupLayers,
} from 'features/libra/stepComponents/niiReport/zoneUtils'
import { Plus } from 'lucide-react'
import { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoMarkupRect, GeckoMeasurementUnit } from 'types'
import { v4 } from 'uuid'

const AssetVizContainer = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border-radius: 8px;
  overflow: hidden;
`

const ZonesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ZoneRow = styled.div`
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  overflow: hidden;
`

const ZoneMainRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
`

const ZoneInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`

const ZoneName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
  flex: 1;
`

const CoverageStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.forecast.background3};
  border-radius: 6px;
`

const TopControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const LeftControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const CoverageStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`

const CoverageValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const CoverageLabel = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ComponentSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ComponentLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-weight: 500;
`

const ZonesListComponent: FC<{
  zones: NiiSourcesZoneParams[]
  editingZoneId?: string
  totalAssetArea?: import('types').GeckoMeasurement
  onDeleteZone: (zoneId: string) => void
  onEditZone: (zoneId: string) => void
  onUpdateZone: (zoneId: string, updates: Partial<NiiSourcesZoneParams>) => void
  onCancelEdit: () => void
}> = ({
  zones,
  editingZoneId,
  totalAssetArea,
  onDeleteZone,
  onEditZone,
  onUpdateZone,
  onCancelEdit,
}) => {
  if (zones.length === 0) {
    return (
      <ZonesList>
        <ZoneRow>
          <ZoneMainRow>
            <ZoneInfo>
              <ZoneName>No zones defined</ZoneName>
            </ZoneInfo>
            <Tag color='default'>Use &quot;Add Zone&quot; to create zones</Tag>
          </ZoneMainRow>
        </ZoneRow>
      </ZonesList>
    )
  }

  return (
    <ZonesList>
      {zones.map((zone) => (
        <ZoneRowComponent
          key={zone.id}
          zone={zone}
          isEditing={editingZoneId === zone.id}
          totalAssetArea={totalAssetArea}
          onEdit={() => onEditZone(zone.id)}
          onDelete={() => onDeleteZone(zone.id)}
          onUpdate={(updates: Partial<NiiSourcesZoneParams>) =>
            onUpdateZone(zone.id, updates)
          }
          onCancelEdit={onCancelEdit}
        />
      ))}
    </ZonesList>
  )
}

export const AssetVisualizationPanel: FC = () => {
  const {
    niiData,
    setNiiData,
    assetModelData: { inspectionComponentPairs, assetModel, componentDatasets },
  } = useNiiReportData()

  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [drawingLayer, setDrawingLayer] =
    useState<import('types').GeckoMarkupLayer>()
  const [selectedZoneId, setSelectedZoneId] = useState<string>()
  const [editingZoneId, setEditingZoneId] = useState<string>()
  const [show3D, setShow3D] = useState(false)
  const [selection, setSelection] = useState<
    | import('types').GeckoMarkupLayer
    | import('types').GeckoMarkupPrimitive
    | undefined
  >()

  const [selectedComponentId, setSelectedComponentId] = useState<
    string | undefined
  >(() => inspectionComponentPairs[0]?.component.id)

  // Get the selected component and inspection pair
  const selectedPair = useMemo(() => {
    return inspectionComponentPairs.find(
      (pair) => pair.component.id === selectedComponentId
    )
  }, [inspectionComponentPairs, selectedComponentId])

  const selectedComponent = useMemo(() => {
    return selectedPair ? [selectedPair.component] : []
  }, [selectedPair])

  // Get component data frame for selected component
  const selectedComponentDataFrame = useMemo(() => {
    return componentDatasets.find(
      (ds) => ds.tags.component === selectedComponentId
    )
  }, [componentDatasets, selectedComponentId])

  // Get inspection for selected component
  const selectedInspection = useMemo(() => {
    return selectedPair?.inspection
  }, [selectedPair])

  // Filter zones to only show those for the selected component
  const allZones = useMemo(
    () => niiData?.zoneParams ?? [],
    [niiData?.zoneParams]
  )
  const zonesForSelectedComponent = useMemo(() => {
    if (!selectedComponentId) return []
    return allZones.filter((zone) => zone.componentId === selectedComponentId)
  }, [allZones, selectedComponentId])

  // Convert selected component zones to markup layers for visualization
  const zoneMarkupLayers = useMemo(() => {
    if (!selectedComponentId) return []
    return zonesToMarkupLayers(
      zonesForSelectedComponent,
      selectedComponentId,
      'org',
      'site',
      'unit'
    )
  }, [zonesForSelectedComponent, selectedComponentId])

  // Combine zone layers with drawing layer
  const allMarkupLayers = useMemo(() => {
    const layers = [...zoneMarkupLayers]
    if (drawingLayer) layers.push(drawingLayer)
    return layers
  }, [zoneMarkupLayers, drawingLayer])

  // Calculate total asset area for coverage percentages for selected component
  const totalAssetArea = useMemo(() => {
    if (!assetModel || !selectedComponentId) return undefined
    const componentModel = assetModel.data.components.get(selectedComponentId)
    return componentModel ? computeArea(componentModel) : undefined
  }, [assetModel, selectedComponentId])

  const totalCoverage = useMemo(() => {
    if (!totalAssetArea) return undefined
    const totalZoneArea = zonesForSelectedComponent.reduce(
      (sum, zone) => sum + (zone.calculated_area_sq_m || 0),
      0
    )
    const totalAssetAreaSqM = totalAssetArea.convertTo(
      GeckoMeasurementUnit.SquareMeters
    ).num
    return totalAssetAreaSqM > 0 ? (totalZoneArea / totalAssetAreaSqM) * 100 : 0
  }, [zonesForSelectedComponent, totalAssetArea])

  // Zone drawing handlers
  const handleAddZone = useCallback(() => {
    if (!selectedComponentId) return

    const measurementUnit =
      selectedComponentDataFrame?.data?.geometry?.x?.unit?.id || 'mm'

    const newDrawingLayer = createDrawingLayer(
      selectedComponentId,
      'org', // dummy org ID
      'site', // dummy site ID
      'unit', // dummy unit ID
      measurementUnit
    )

    setDrawingLayer(newDrawingLayer)
    setIsDrawingMode(true)
    setSelectedZoneId(undefined)
    setSelection(newDrawingLayer) // Set the drawing layer as selection
  }, [selectedComponentId, selectedComponentDataFrame])

  const handleZoneDrawn = useCallback(
    (primitive: import('types').GeckoMarkupPrimitive): Promise<void> => {
      // Check if primitive is a rectangle
      const isRect =
        primitive && primitive.constructor.name === 'GeckoMarkupRect'

      if (!isRect || !selectedComponentId) {
        return Promise.resolve()
      }

      const newZone: NiiSourcesZoneParams = {
        id: v4().substring(0, 6),
        title: `Zone ${zonesForSelectedComponent.length + 1}`,
        componentId: selectedComponentId,
        bounds: markupRectToZoneBounds(primitive as GeckoMarkupRect),
        calculated_area_sq_m: calculateZoneArea(
          (primitive as GeckoMarkupRect).rect
        ),
        color: '#3B82F6', // Default blue color
      }

      setNiiData((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          zoneParams: [...(prev.zoneParams ?? []), newZone],
        }
      })

      // Exit drawing mode
      setIsDrawingMode(false)
      setDrawingLayer(undefined)
      setSelectedZoneId(newZone.id)

      return Promise.resolve()
    },
    [selectedComponentId, zonesForSelectedComponent.length, setNiiData]
  )

  const handleZoneClick = useCallback(
    (
      clickedSelection:
        | import('types').GeckoMarkupPrimitive
        | import('types').GeckoComponentShape
        | import('types').GeckoFeature
        | undefined
    ) => {
      if (!clickedSelection || isDrawingMode) return

      // Map markup layer back to zone ID - only GeckoMarkupPrimitive has markupLayerID
      if (
        'markupLayerID' in clickedSelection &&
        clickedSelection.markupLayerID
      ) {
        const zoneId = clickedSelection.markupLayerID.replace('zone-', '')
        setSelectedZoneId(zoneId)
      }
    },
    [isDrawingMode]
  )

  // Zone CRUD handlers
  const handleDeleteZone = useCallback(
    (zoneId: string) => {
      setNiiData((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          zoneParams: prev.zoneParams?.filter((z) => z.id !== zoneId),
        }
      })

      // Clear selection if deleting selected zone
      if (selectedZoneId === zoneId) {
        setSelectedZoneId(undefined)
      }
    },
    [setNiiData, selectedZoneId]
  )

  const handleEditZone = useCallback((zoneId: string) => {
    setEditingZoneId(zoneId)
  }, [])

  const handleUpdateZone = useCallback(
    (zoneId: string, updates: Partial<NiiSourcesZoneParams>) => {
      setNiiData((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          zoneParams: prev.zoneParams?.map((z) =>
            z.id === zoneId ? { ...z, ...updates } : z
          ),
        }
      })

      setEditingZoneId(undefined)
    },
    [setNiiData]
  )

  return (
    <WebGLLayer>
      <TopControls>
        <LeftControls>
          {/* Component selector - only show if multiple components */}
          {inspectionComponentPairs.length > 1 && (
            <ComponentSelector>
              <ComponentLabel>Component:</ComponentLabel>
              <Select
                style={{ minWidth: 200 }}
                value={selectedComponentId}
                onChange={setSelectedComponentId}
                placeholder='Select a component'>
                {inspectionComponentPairs.map(({ component }) => (
                  <Select.Option key={component.id} value={component.id}>
                    {component.data.name || component.id}
                  </Select.Option>
                ))}
              </Select>
            </ComponentSelector>
          )}

          <CantileverButton
            onClick={handleAddZone}
            disabled={isDrawingMode || !selectedComponentId}
            primary={!isDrawingMode}>
            <Plus size={16} style={{ marginRight: '4px' }} />
            {isDrawingMode ? 'Drawing...' : 'Add Zone'}
          </CantileverButton>

          <CantileverButton
            onClick={() => setShow3D(!show3D)}
            style={{ padding: '6px 12px' }}
            title={show3D ? 'Switch to 2D view' : 'Switch to 3D view'}>
            <DimensionsIcon show3D={show3D} />
          </CantileverButton>
        </LeftControls>

        {/* Coverage KPIs on the right */}
        {zonesForSelectedComponent.length > 0 &&
          totalCoverage !== undefined && (
            <CoverageStats>
              <CoverageStat>
                <CoverageValue>{totalCoverage.toFixed(1)}%</CoverageValue>
                <CoverageLabel>Total Coverage</CoverageLabel>
              </CoverageStat>
              <CoverageStat>
                <CoverageValue>
                  {zonesForSelectedComponent.length}
                </CoverageValue>
                <CoverageLabel>Zones Defined</CoverageLabel>
              </CoverageStat>
              {totalAssetArea && (
                <CoverageStat>
                  <CoverageValue>
                    {totalAssetArea
                      .convertTo(GeckoMeasurementUnit.SquareMeters)
                      .num.toFixed(1)}
                  </CoverageValue>
                  <CoverageLabel>Total Asset Area (m²)</CoverageLabel>
                </CoverageStat>
              )}
            </CoverageStats>
          )}
      </TopControls>

      {assetModel && selectedComponentId ? (
        <AssetVizContainer>
          <AssetViz
            assetModel={assetModel}
            components={selectedComponent}
            componentDataFrames={
              selectedComponentDataFrame ? [selectedComponentDataFrame] : []
            }
            selectedInspection={selectedInspection}
            markupLayers={allMarkupLayers}
            selection={selection}
            mode={
              isDrawingMode
                ? AssetVizMode.CreateMarkupRect
                : AssetVizMode.SelectBin
            }
            onWrite={handleZoneDrawn}
            onSelected={handleZoneClick}
            sceneConfig={{
              show3D: show3D,
              showFeatures: true,
              heatmapOpacity: 1,
            }}
          />
        </AssetVizContainer>
      ) : (
        <div
          style={{
            height: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666',
          }}>
          {!assetModel
            ? 'No asset model available for visualization'
            : 'Select a component to view and define zones'}
        </div>
      )}

      <div>
        <h4 style={{ marginBottom: '12px', color: '#333' }}>
          Defined Zones
          {inspectionComponentPairs.length > 1 &&
            selectedComponentId &&
            ` (${selectedPair?.component.data.name || selectedComponentId})`}
        </h4>
        <ZonesListComponent
          zones={zonesForSelectedComponent}
          editingZoneId={editingZoneId}
          totalAssetArea={totalAssetArea}
          onDeleteZone={handleDeleteZone}
          onEditZone={handleEditZone}
          onUpdateZone={handleUpdateZone}
          onCancelEdit={() => setEditingZoneId(undefined)}
        />
      </div>
    </WebGLLayer>
  )
}
